import React, { type ReactElement, useEffect, useState } from 'react';
import { InterPoint } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Chip,
  DateAndTimePicker,
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { addYears } from 'date-fns';

import { testIds } from '../../../../../tests';
import {
  CF_NEU_BLD,
  CF_NEU_INV,
  CF_NEU_STL,
  CF_SEC_BLD,
  CF_SEC_MED,
} from '../../../../constants';
import { useInvertedLocalize } from '../../../../hooks';
import { MOCK_PREDEFINED_COMMENTS } from '../mockMitigateList';
import {
  type DateTypeOptions,
  type MitigateItemType,
} from '../SodMitigateModal';

import './SodMitigateListItem.css';

interface SodMitigateListItemProps {
  item: MitigateItemType;
  onToggleSelect: (title: string) => void;
  handleDateSelection: (
    id: string,
    date: Date,
    dateType: DateTypeOptions
  ) => void;
  handleCommentChange: (id: string, comment: string) => void;
  isFormCritical: boolean;
}

/**
 * * TODO
 * - Write a description
 * - Write IT's
 * - Find out the AC for max data
 * - DateAndTimePicker crit state: isCritical={isDateCritical)
 * - Localize these labels
 */
function SodMitigateListItem({
  item,
  onToggleSelect,
  handleDateSelection,
  handleCommentChange,
  isFormCritical,
}: SodMitigateListItemProps): ReactElement {
  const {
    id,
    title,
    isSelected,
    selectedDates: { startDate, endDate },
    comment,
    // isCritical,
    isRecommended,
    priority,
    mitigateType,
    description,
    expirationDate,
  } = item;
  const [isSelectionNoticeVisible, setIsSelectionNoticeVisible] =
    useState(false);
  // Selected Date Range
  const minDate = new Date(); // Find out the AC for the min Date
  const rangeMaxDate = addYears(new Date(), 1); // Find out the AC for the selected date range
  const l = useInvertedLocalize();

  const itemClasses = cx(
    'SodMitigateListItem',
    isSelected && 'SodMitigateListItem--isSelected'
  );
  const selectedButtonWrapperClasses = cx(
    'SodMitigateListItem-selectButtonWrapper',
    isSelectionNoticeVisible &&
      'SodMitigateListItem-selectButtonWrapper--isSelectionNoticeVisible'
  );

  // Set Start Date on mount to NOW
  useEffect(() => {
    handleDateSelection(id, new Date(), 'startDate');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectClick = () => {
    onToggleSelect(id);
    setIsSelectionNoticeVisible(!isSelectionNoticeVisible);
  };

  const handleStartDateChange = (date: Date) => {
    handleDateSelection(id, date, 'startDate');
  };

  const handleEndDateChange = (date: Date) => {
    handleDateSelection(id, date, 'endDate');
  };

  const getPriorityChipColor = () => {
    switch (priority) {
      case 'High':
        return 'critical';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'success';
      default:
        return 'info';
    }
  };

  const getCommentBlock = () => (
    <Box className='SodMitigateListItem-commentBlock'>
      <InputField
        name='mitigation-submit-comment'
        kind='multiline'
        label={
          <Typography kind='label' htmlFor='mitigation-submit-comment'>
            {l('Comments')}
          </Typography>
        }
        value={comment}
        setValue={(newComment: string) => handleCommentChange(id, newComment)}
        predefinedOptions={MOCK_PREDEFINED_COMMENTS}
        predefinedOptionsConfig={{ lineLimit: 1 }}
        placeholder={l('Provide a reason for your decision')}
        isRequired={comment.length < 1 || isFormCritical}
        textAreaMaxHeight='6.625rem'
        isCritical={isFormCritical}
        // setIsCritical={setCritical}
        CriticalHelperText={
          <InlineMessage kind='missingRequiredField' size='small' />
        }
      />
    </Box>
  );

  return (
    <li className={itemClasses}>
      {/* Main Details */}
      <Box className='SodMitigateListItem-detailsWrapper'>
        <Box className='SodMitigateListItem-details'>
          <Typography
            kind='h3'
            color={CF_SEC_BLD}
            className='SodMitigateListItem-title'>
            {title}
          </Typography>
          <Box className='SodMitigateListItem-subDetails'>
            {/* Priority Block */}
            <Box className='SodMitigateListItem-priorityBlock'>
              <Typography kind='body2' color={CF_NEU_STL}>
                {l('Priority:')}
              </Typography>
              <Chip
                kind='subtleFilled'
                size='xSmall'
                color={getPriorityChipColor()}
                label={priority} // needs to be capitalized
                isInteractionDisabled
              />
            </Box>
            <InterPoint color='neutralBold' font='body2' />
            {/* Type Block */}
            <Box className='SodMitigateListItem-typeBlock'>
              <Typography kind='body2' color={CF_NEU_STL}>
                {l('Type:')}
              </Typography>
              <Typography kind='body2' color={CF_NEU_BLD}>
                {l(mitigateType)}
              </Typography>
            </Box>
            <InterPoint color='neutralBold' font='body2' />
            {/* Expiration Block */}
            <Box className='SodMitigateListItem-expireBlock'>
              <Typography kind='body2' color={CF_NEU_STL}>
                {l('Expires:')}
              </Typography>
              <Typography kind='body2' color={CF_NEU_BLD}>
                {expirationDate}
              </Typography>
            </Box>
            <InterPoint color='neutralBold' font='body2' />
            {/* Description Block */}
            <Box className='SodMitigateListItem-descriptionBlock'>
              <Typography
                kind='body2'
                color={CF_NEU_STL}
                lineLimit={1}
                className='SodMitigateListItem-description'>
                {description}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className={selectedButtonWrapperClasses}>
          <Typography
            kind='body2'
            color={CF_SEC_MED}
            className='SodMitigateListItem-selectedNotice'>
            {isSelected ? l('Selected') : ''}
          </Typography>
          <ButtonIcon
            size='large'
            kind='outlinedSecondary'
            isSelected={isSelected}
            icon={<Icon kind={isSelected ? 'Subtract' : 'Add'} />}
            onClick={handleSelectClick}
            // eslint-disable-next-line no-warning-comments
            // TODO: Localize these labels
            ariaLabel='Toggle Item Selection'
          />
        </Box>
      </Box>
      {/* Date Selection Expand / Collapse Area */}
      <Box className='SodMitigateListItem-collapsingWrapper'>
        {isSelected && (
          <>
            <Box className='SodMitigateListItem-dateSelection'>
              <DateAndTimePicker
                kind='dateAndTimeStart'
                dateFormat='MM/dd/yyyy, h:mm aa'
                label={l('Start Date')}
                isDisabled={!isSelected}
                startDate={startDate}
                setStartDate={handleStartDateChange}
                endDate={endDate}
                maxDate={rangeMaxDate}
                minDate={minDate}
                isRequired
                // isCritical={isDateCritical}
                portalId='root'
              />
              <DateAndTimePicker
                kind='dateEnd'
                label={l('End Date')}
                isDisabled={!isSelected}
                startDate={startDate}
                setStartDate={handleStartDateChange}
                endDate={endDate}
                setEndDate={handleEndDateChange}
                maxDate={rangeMaxDate}
                minDate={minDate}
                isRequired
                // isCritical={isDateCritical}
                portalId='root'
              />
            </Box>
            {getCommentBlock()}
          </>
        )}
      </Box>
      {/* Comment Area */}
      {isRecommended && (
        <span className='SodMitigateListItem-recommendedStar'>
          <Icon
            kind='StarFilled'
            size='smallMedium'
            color={CF_NEU_INV}
            className='SodMitigateListItem-recommendedStarIcon'
            dataTestId={testIds.SOD_MITIGATE_RECOMMENDED_STAR}
          />
        </span>
      )}
    </li>
  );
}

export default SodMitigateListItem;
